import { ConfirmButtonConfig } from '@app/types';
import { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { useLocation, useNavigate, UNSAFE_NavigationContext } from 'react-router-dom';

/*
UseNavigationConfirmationConfig options:
hasUnsavedChanges -> Whether or not a user's current UI state is persisted, defaults to false (navigation not blocked)
unsavedChangesMsg -> Message to display to user in confirmation modal, defaults to 'If you exit, you will lose any
		unsaved changes'
saveChanges -> Optional function for dispatching a save of any unsaved data in the UI. If provided, a "save" button is
		included in the navigation confirmation modal
onExit -> Optional function for consumer to perform some cleanup prior to navigation occurring if user chooses to exit
backBtnOverrideFn -> Optional function for overriding back button functionality, e.g. if the back button should return
		a user to a "view only" state from the "editing" state. We still display the confirmation modal on back button click
		if this option is set, but the end result of a user clicking "exit" will be to call this function rather than
		`history.goBack`.
*/
interface UseNavigationConfirmationConfig {
  hasUnsavedChanges: boolean;
  unsavedChangesMsg?: string;
  saveChanges?: () => void;
  onExit?: () => void;
  backBtnOverrideFn?: () => void;
}

const saveAction: ConfirmButtonConfig = {
  title: 'Save Changes',
  value: 'save',
  variant: 'primary',
};
const exitAction: ConfirmButtonConfig = {
  title: 'Exit',
  value: 'exit',
  variant: 'danger',
  autoFocus: true,
};

export const useNavigationConfirmation = (
  {
    hasUnsavedChanges = false,
    unsavedChangesMsg = 'If you exit, you will lose any unsaved changes',
    saveChanges,
    backBtnOverrideFn,
    onExit,
  }: UseNavigationConfirmationConfig,
  deps: Array<any> = [],
) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const didExit = useRef(false);
  const clickedBackBtn = useRef(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const modalActions = useMemo(
    () => (saveChanges ? [saveAction, exitAction] : [exitAction]),
    [saveChanges],
  );

  useEffect(() => {
    if (didExit.current) {
      return;
    }

    if (hasUnsavedChanges && !location.state?.navBlock) {
      return navigate(location.pathname, { state: { navBlock: true } });
    }

    if (!saveInProgress && !hasUnsavedChanges && location.state?.navBlock) {
      return navigate(-1);
    }
  }, [hasUnsavedChanges, location, saveInProgress, navigate]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      const push = navigator.push;
      const go = navigator.go;
      const onpopstate = window.onpopstate;

      const cleanup = () => {
        didExit.current = true;
        navigator.push = push;
        navigator.go = go;
        window.onpopstate = onpopstate;
      };

      const createNavBlocker = (continueToDestination) => {
        window.app.confirm(
          {
            title: 'Exit Without Saving?',
            message: unsavedChangesMsg,
            actions: modalActions,
          },
          (action: string) => {
            if (action == 'exit') {
              cleanup();
              onExit?.();
              continueToDestination();
            } else if (action == 'save') {
              if (saveChanges) {
                setSaveInProgress(true);
                return Promise.resolve(saveChanges()).then(continueToDestination);
              }
            }
            return undefined;
          },
        );
      };

      navigator.push = (...args: Parameters<typeof push>) => createNavBlocker(() => push(...args));
      navigator.go = () =>
        createNavBlocker(() => {
          if (backBtnOverrideFn) {
            backBtnOverrideFn();
          } else {
            navigate(clickedBackBtn.current ? -1 : -2);
          }
        });
      // When browser back btn is used, we call navigate to trigger navigator.go, which will trigger the nav block
      window.onpopstate = () => {
        clickedBackBtn.current = true;
        navigate(-1);
      };

      return cleanup;
    }

    return () => {};
  }, [...deps, hasUnsavedChanges, location, navigate]);
};
